import { useEffect, useState } from "react";
import { api, API, formatApiError, formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Search, Filter, Download } from "lucide-react";

const ACTION_BADGES = {
  login: { label: "Signed in", color: "bg-[#E8ECE7] text-[#166534]" },
  logout: { label: "Signed out", color: "bg-[#F1F5F9] text-[#64748B]" },
  create_care_seeker: { label: "Added Care Seeker", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  update_care_seeker: { label: "Updated Care Seeker", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  delete_care_seeker: { label: "Deleted Care Seeker", color: "bg-[#FEE2E2] text-[#991B1B]" },
  create_intake: { label: "Created intake", color: "bg-[#E8ECE7] text-[#1E3A8A]" },
  create_note: { label: "Added note", color: "bg-[#FEF3C7] text-[#92400E]" },
  delete_note: { label: "Deleted note", color: "bg-[#FEE2E2] text-[#991B1B]" },
  create_referral: { label: "Created referral", color: "bg-[#E0E7FF] text-[#3730A3]" },
  update_referral: { label: "Updated referral", color: "bg-[#E0E7FF] text-[#3730A3]" },
  create_resource: { label: "Added resource", color: "bg-[#E8ECE7] text-[#166534]" },
  create_outreach: { label: "Logged outreach", color: "bg-[#FEF3C7] text-[#92400E]" },
  create_maternal: { label: "Maternal visit", color: "bg-[#FCE7F3] text-[#9D174D]" },
  create_task: { label: "Created task", color: "bg-[#E0E7FF] text-[#3730A3]" },
  create_user: { label: "Added user", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  update_user: { label: "Updated user", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  delete_user: { label: "Removed user", color: "bg-[#FEE2E2] text-[#991B1B]" },
};

const ALL_ACTIONS = Object.keys(ACTION_BADGES);

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (actionFilter) params.action = actionFilter;
      const { data } = await api.get("/audit-log", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [actionFilter]);

  const badge = (action) =>
    ACTION_BADGES[action] || { label: action.replace(/_/g, " "), color: "bg-[#F1F5F9] text-[#64748B]" };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Activity trail</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Audit Log</h1>
          <p className="text-[#334155] mt-2 max-w-2xl">
            A chronological record of every meaningful action taken in HarborWell Connect. Helpful for
            accountability, audits, and understanding the rhythm of your work.
          </p>
        </div>
        <button
          onClick={() => {
            fetch(`${API}/exports/audit-log.csv`, { credentials: "include" })
              .then((r) => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
              .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "audit_log.csv";
                a.click();
                URL.revokeObjectURL(a.href);
                toast.success("Audit log downloaded");
              })
              .catch(() => toast.error("Export failed"));
          }}
          data-testid="export-audit-csv-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
        >
          <Download className="w-4 h-4" /> Download CSV
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-[#1E3A8A]" />
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          data-testid="audit-action-filter"
          className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm flex-1 sm:flex-initial"
        >
          <option value="">All actions</option>
          {ALL_ACTIONS.map((a) => (
            <option key={a} value={a}>{ACTION_BADGES[a].label}</option>
          ))}
        </select>
        <span className="text-xs text-[#1E3A8A] ml-auto">{items.length} most recent</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center">
          <ShieldCheck className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No activity recorded</h3>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="audit-log-table">
            <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">When</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Who</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Action</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden md:table-cell">Target</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const b = badge(e.action);
                return (
                  <tr key={e.id} className="border-t border-[#F0EFEB]">
                    <td className="px-4 py-3 text-[#334155] whitespace-nowrap text-xs">{formatDateTime(e.timestamp)}</td>
                    <td className="px-4 py-3">
                      <div className="text-[#0F172A] font-medium text-sm">{e.user_email || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider ${b.color}`}>
                        {b.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#1E3A8A] hidden md:table-cell">
                      {e.entity_type ? (
                        <span>
                          <span className="capitalize">{e.entity_type.replace(/_/g, " ")}</span>
                          {e.entity_id && <span className="text-[#0F172A]/40"> · {e.entity_id.slice(0, 8)}</span>}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
